import { useAbortController } from "../hooks/useAbortController";
import axios from "axios";
import {getHeadersConfig} from "../utils/config";
import PerformanceStatsFiltersPanel from "../panels/PerformanceStatsFiltersPanel";
import "bootstrap/dist/css/bootstrap.min.css";
import ErrorHandler from "../utils/ErrorHandler";
import {PageName, TrafficSourceResponseDto, DemandSourcesResponseDto} from "../utils/types";
import {SearchResponseDto,ResponseStatus} from "../utils/types";
import {PagesHelper} from "../common/PagesHelper";
import StateHelper from "../utils/StateHelper";
import { FiltersHelper } from "../common/FiltersHelper";
import { joinURLs } from "../utils/urlUtil";
import { TrafficSources } from "../services/TrafficSources";
import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import urlParser from "../utils/urlParser";
import { loadPerformancePageColumns } from "../utils/performanceConfig";
import { Alert } from "react-bootstrap";
import { ColumnIds } from "../utils/types";
import { PerformanceStatsPivotTable } from "../components/PerformanceStatsPivotTable";
import { pivotData } from "../utils/pivotHelper";
import Loading from "../utils/Loding";
import dayjs from "../utils/dayjs";

const clear = (setSearchResponseDto:any) => {
  setSearchResponseDto(null);
};

/**
 * Generate date columns for empty data scenarios
 * Creates an array of date strings based on the date range and mode
 */
const generateDateColumns = (dateStart: any, dateEnd: any, dateMode: string): string[] => {
  const columns: string[] = [];

  if (!dateStart || !dateEnd) {
    return columns;
  }

  const start = dayjs(dateStart);
  const end = dayjs(dateEnd);

  if (!start.isValid() || !end.isValid()) {
    return columns;
  }

  let current = start;

  if (dateMode === 'months') {
    // Generate month columns (YYYY-MM format)
    while (!current.isAfter(end, 'month')) {
      columns.push(current.format('YYYY-MM'));
      current = current.add(1, 'month');
    }
  } else if (dateMode === 'weeks') {
    // Generate week columns (YYYY-WW format)
    // Only include weeks that fall within the date range
    const endYear = end.year();
    const endWeek = end.isoWeek();

    while (true) {
      const year = current.year();
      const week = current.isoWeek();

      // Check if current week is after the end week
      if (year > endYear || (year === endYear && week > endWeek)) {
        break;
      }

      columns.push(`${year}-W${String(week).padStart(2, '0')}`);
      current = current.add(1, 'week');
    }
  } else {
    // Generate daily columns (YYYY-MM-DD format)
    while (!current.isAfter(end, 'day')) {
      columns.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    }
  }

  // Sort in descending order (most recent first)
  return columns.reverse();
};

/**
 * Fetch performance statistics (publishers or demand) from the reporting API
 * - Publisher view: Uses /2/stats/overall endpoint (faster, optimized for publisher aggregations)
 * - Demand view: Uses /2/stats/products endpoint with DemandSummaryReport flag
 * - Publisher view: Groups by date + apptapPublisherId + apptapSiteId
 * - Demand view: Groups by date + campaignSource with DemandSummaryReport flag
 */
const stats_performanceStats_GET = (
  currentUser:any,
  setSearchResponseDto:any,
  updateHistory:boolean = true,
  abortController?: AbortController
) => {
  // Get the current date mode and view mode from page state
  const pageState = StateHelper.getPageState(PageName.performanceStats);
  const dateMode = pageState.dateMode || 'days';
  const viewMode = pageState.performanceStatsViewMode || 'trafficSource';

  // Map dateMode to ColumnIds (days → date, weeks → week, months → month)
  const dateModeToColumnId: Record<string, string> = {
    'days': ColumnIds.date,
    'weeks': ColumnIds.week,
    'months': ColumnIds.month
  };
  const dateGroupBy = dateModeToColumnId[dateMode] || ColumnIds.date;

  // Update pageState.groupBy before pushing to history so URL matches the request
  if (viewMode === 'demand') {
    pageState.groupBy = `${dateGroupBy},campaignSource`;
  } else {
    pageState.groupBy = `${dateGroupBy},apptapPublisherId,apptapSiteId`;
  }

  if (updateHistory) PagesHelper.historyPushFromPageState();

  let queryParams = PagesHelper.getQueryParameters(PageName.performanceStats);
  const config = getHeadersConfig();

  // Use different endpoints based on view mode for optimal performance
  // Traffic Source view: /2/stats/overall is much faster for traffic source aggregations
  // Demand view: /2/stats/products supports DemandSummaryReport flag
  const apiUrl = viewMode === 'demand'
    ? joinURLs("2/stats/products", process.env.REACT_APP_REPORTING_API_BASE_URL)
    : joinURLs("2/stats/overall", process.env.REACT_APP_REPORTING_API_BASE_URL);

  // Configure flags and remove irrelevant filter parameters for each view
  if (viewMode === 'demand') {
    // Demand view: set flag
    queryParams.queryFlags = "DemandSummaryReport";

    // Remove publisher/site filter params (not applicable to demand view)
    delete (queryParams as any)['publisherIdsInclude'];
    delete (queryParams as any)['publisherIdsExclude'];
    delete (queryParams as any)['siteIdsInclude'];
    delete (queryParams as any)['siteIdsExclude'];
  } else {
    // Publisher view: remove demand source filter params (not applicable)
    delete (queryParams as any)['demandSourceIdsInclude'];
    delete (queryParams as any)['demandSourceIdsExclude'];
  }

  const responseDto : SearchResponseDto = {
    status : ResponseStatus.LOADING,
    error : null,
    data : null,
    query : null,
    metadata : null
  };
  setSearchResponseDto(responseDto);

  axios
    .get(apiUrl, {...config, params: queryParams, signal: abortController?.signal })
    .then(function (response: any) {
      if (response.data?.data) {
        const responseDto : SearchResponseDto = response.data;
        responseDto.status = ResponseStatus.OK;
        responseDto.error = null;
        setSearchResponseDto(responseDto);
      } else {
        clear(setSearchResponseDto);
      }
    })
    .catch(function (err) {
      if (!axios.isCancel(err)) {
        clear(setSearchResponseDto);
        const isLoggedOut = ErrorHandler.handleAuthError(err);
        if (!isLoggedOut) {
          const responseDto : SearchResponseDto = {
            status : ResponseStatus.ERROR,
            error : err?.response?.data?.errorMessage,
            data : null,
            query :  null,
            metadata : null
          };
          setSearchResponseDto(responseDto);
        }
      }
    });
};


/**
 * PerformanceStatsPage Component
 *
 * Displays performance statistics for publishers (supply) or demand sources.
 * Supports two view modes with toggle: Publisher View (Supply) and Demand View.
 *
 * Key features:
 * - Uses /2/stats/products endpoint for both views (supports updated publisherId/siteId params)
 * - Publisher View: Groups by apptapPublisherId + apptapSiteId + date/week/month
 * - Demand View: Groups by campaignSource + date/week/month with DemandSummaryReport flag
 * - Client-side pivoting: rows = publishers/demand sources, columns = time periods
 * - Statistical highlighting with arrows for day-over-day and outlier detection
 * - Expandable rows showing detailed metrics breakdown
 * - Contextual filters: traffic sources (publisher view) or demand sources (demand view)
 */
function PerformanceStatsPage(props : any) {
  const { createController: createStatsController } = useAbortController();
  const { createController: createTrafficSourcesController } = useAbortController();
  const { createController: createDemandSourcesController } = useAbortController();
  const location = useLocation();

  const [searchResponseDto, setSearchResponseDto] = useState<SearchResponseDto>(null);
  const [trafficSourceResponseDto, setTrafficSourceResponseDto] = useState<TrafficSourceResponseDto>(null);
  const [demandSourcesResponseDto, setDemandSourcesResponseDto] = useState<DemandSourcesResponseDto>(null);

  // Initialize state with defaults if no params provided (on initial load)
  useEffect(() => {
    // Check if we have no search params (or only empty params)
    const searchParams = new URLSearchParams(location.search);
    const hasParams = searchParams.toString().length > 0;

    if (!hasParams && props.currentUser) {
      // No params - initialize pageState with default date range
      const pageState = StateHelper.getPageState(PageName.performanceStats);

      // Set default date range: last 10 days (10 days total)
      pageState.dateStart = dayjs.utc().subtract(9, 'days').startOf('day');
      pageState.dateEnd = dayjs.utc().endOf('day');
      pageState.dateMode = 'days';
      pageState.performanceStatsViewMode = 'trafficSource';

      // Set groupBy based on view mode (same logic as stats_performanceStats_GET)
      const dateMode = pageState.dateMode || 'days';
      const dateModeToColumnId: Record<string, string> = {
        'days': ColumnIds.date,
        'weeks': ColumnIds.week,
        'months': ColumnIds.month
      };
      const dateGroupBy = dateModeToColumnId[dateMode] || ColumnIds.date;
      pageState.groupBy = `${dateGroupBy},apptapPublisherId,apptapSiteId`;

      // Use the same helper as ProductsPage to ensure required fields are present
      FiltersHelper.checkRequiredGroupByAndPermissions(props.currentUser, PageName.performanceStats);
    }
  }, [location.search, props.currentUser]);

  // Parse URL parameters on mount and when location changes
  useEffect(() => {
    urlParser.parseUrl(location.pathname + location.search, () => {});
  }, [location]);

  // Get view mode from state (synced with URL)
  const pageState = StateHelper.getPageState(PageName.performanceStats);
  const viewMode = pageState.performanceStatsViewMode || 'trafficSource';

  // Load performance-specific columns
  loadPerformancePageColumns();

  function search(updateHistory:boolean = true) {
    const controller = createStatsController();
    stats_performanceStats_GET(props.currentUser, setSearchResponseDto, updateHistory, controller);
  }

  // Handle view mode change (Traffic Source vs Demand)
  function handleViewModeChange(newMode: 'trafficSource' | 'demand') {
    if (newMode === viewMode) return; // No change

    // Update view mode in state
    pageState.performanceStatsViewMode = newMode;

    // Update groupBy based on view mode
    pageState.groupBy = `${ColumnIds.date}`;
    pageState.pivotBy = null;

    // Trigger search with new view mode
    search(true);
  }

  // Register search function with StateHelper
  StateHelper.setSearchFuntion(PageName.performanceStats, search);

  // Sync groupBy with viewMode on mount (we pivot on client side)
  useEffect(() => {
    // Ensure groupBy matches the current viewMode
    if (viewMode === 'trafficSource' || viewMode === 'demand') {
      // Both views get daily aggregated data
      pageState.groupBy = `${ColumnIds.date}`;
      pageState.pivotBy = null;
    }
  }, [viewMode, pageState]);

  useEffect(() => {
    if (props.currentUser) {
      TrafficSources.getTrafficSources(createTrafficSourcesController(), setTrafficSourceResponseDto);
      PagesHelper.loadDemandSourcesHelper(createDemandSourcesController(), setDemandSourcesResponseDto);
      const statsController = createStatsController();
      stats_performanceStats_GET(props.currentUser, setSearchResponseDto, false, statsController);
    }
  }, [props.currentUser, createStatsController, createTrafficSourcesController, createDemandSourcesController]);

  // Transform API data: pivot from flat data to table structure
  const { pivotedData, dateColumns } = useMemo(() => {
    const rawData = searchResponseDto?.data || [];

    // Determine the date column key based on dateMode
    const dateMode = pageState.dateMode || 'days';
    let columnKey = 'date'; // default to daily
    if (dateMode === 'months') {
      columnKey = 'month';
    } else if (dateMode === 'weeks') {
      columnKey = 'week';
    }

    // Always generate the complete set of expected date columns from the date range
    const expectedDateColumns = generateDateColumns(
      pageState.dateStart,
      pageState.dateEnd,
      dateMode
    );

    // If no data, return empty pivoted data with the expected date columns
    if (rawData.length === 0) {
      return { pivotedData: [], dateColumns: expectedDateColumns };
    }

    // Pivot the data based on view mode, passing expectedColumns to ensure all dates show
    let result;
    if (viewMode === 'demand') {
      // Demand view: rows = campaignSource, columns = dates/weeks/months
      result = pivotData(rawData, {
        rowKeys: ['campaignSource'],
        columnKey: columnKey,
        valueKey: 'revenueApptap',
        labelKeys: [],
        expectedColumns: expectedDateColumns
      });
    } else {
      // Publisher view: rows = publisher-site, columns = dates/weeks/months
      result = pivotData(rawData, {
        rowKeys: ['apptapPublisherId', 'apptapSiteId'],
        columnKey: columnKey,
        valueKey: 'revenueApptap',
        labelKeys: ['apptapPublisherName', 'apptapSiteName'],
        expectedColumns: expectedDateColumns
      });
    }

    return { pivotedData: result.data, dateColumns: result.dateColumns };
  }, [searchResponseDto, pageState.dateMode, viewMode, pageState.dateStart, pageState.dateEnd]);

  return (
    <>
      <PerformanceStatsFiltersPanel
        search={search}
        page={PageName.performanceStats}
        currentUser={props.currentUser}
        trafficSourceResponseDto={trafficSourceResponseDto}
        demandSourcesResponseDto={demandSourcesResponseDto}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />
      <hr/>

      {/* Show loading state */}
      {searchResponseDto?.status === ResponseStatus.LOADING && (
        <Loading />
      )}

      {/* Show error state */}
      {searchResponseDto?.status === ResponseStatus.ERROR && (
        <Alert variant="danger">
          Error loading data: {searchResponseDto.error || 'Unknown error'}
        </Alert>
      )}

      {/* Show the performance stats pivot table */}
      {searchResponseDto?.status === ResponseStatus.OK && (() => {
        // Determine the date column key based on dateMode
        const dateMode = pageState.dateMode || 'days';
        let dateColumnKey = 'date'; // default to daily
        if (dateMode === 'months') {
          dateColumnKey = 'month';
        } else if (dateMode === 'weeks') {
          dateColumnKey = 'week';
        }

        return (
          <PerformanceStatsPivotTable
            data={pivotedData}
            rawData={searchResponseDto?.data || []}
            dateColumns={dateColumns}
            viewMode={viewMode}
            trafficSources={trafficSourceResponseDto?.publishersArray || []}
            demandSources={demandSourcesResponseDto?.demandSourcesResponse || []}
            dateColumnKey={dateColumnKey}
          />
        );
      })()}
    </>
  );
}

export default PerformanceStatsPage;